import { message, Skeleton } from "antd";
import { isBefore, startOfDay } from "date-fns";
import {
    AttachedFiles,
    AttachedFilesRequest,
    BTFileSystem,
    type FileViewOptions,
} from "legacyComponents/FileUploadContainer.types";
import {
    getFileViewOptionDefaults,
    getPermissionsOverride,
} from "legacyComponents/FileUploadContainer.utils";
import {
    type MouseEvent,
    useCallback,
    useContext,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from "react";
import { createPortal } from "react-dom";
import { type RouteComponentProps } from "react-router";
import { useNavigate, useSearchParams } from "react-router-dom-v5-compat";

import { IconArrowSquareOut, IconSparkle, IconTrash } from "@btnet/blueprint-icons";
import { BTInput, BTLabel, BTSection, BTText, BTTextArea } from "@btnet/core-components";
import { BTPopover } from "@btnet/core-components/BTPopover";
import { ChangeOrderStatus } from "@btnet/services-btnet/handlers/models/changeOrderStatus";
import { EntityType } from "@btnet/services-btnet/handlers/models/entityType";
import { FeedbackTarget } from "@btnet/services-btnet/handlers/models/feedbackTarget";
import type { LineItem } from "@btnet/services-btnet/handlers/models/lineItem";
import type { ParsedUrlSelectionOptionFields } from "@btnet/services-btnet/handlers/models/parsedUrlSelectionOptionFields";
import type { SelectionOptionGroup } from "@btnet/services-btnet/handlers/models/selectionOptionGroup";
import { Statuses } from "@btnet/services-btnet/handlers/models/statuses";
import { selectionOptionsParseDocumentsPostV2 } from "@btnet/services-btnet/handlers/selection-options/selection-options";
import { cn } from "@btnet/utilities-core";

import { BdsLayout, BdsLink, BdsSection } from "@buildertrend/components";

import { type BuilderInfo } from "helpers/AppProvider.types";
import { AppDefaultInfoContext } from "helpers/globalContext/AppDefaultInfoContext";
import { BuilderInfoContext } from "helpers/globalContext/BuilderInfoContext";
import { useRequiredContext } from "helpers/globalContext/useRequiredContext";
import { UserInfoContext } from "helpers/globalContext/UserInfoContext";

import { BTSelectItem } from "types/apiResponse/apiResponse";
import { BTLoginTypes, DocumentInstanceType, EntityTypes } from "types/enum";

import { getEditModeLink } from "utilities/editMode/edit.utility";
import { reportError } from "utilities/errorHelpers";
import yup from "utilities/form/yup";
import { getTaxGroupSelectItemsFromLineItemDefaults } from "utilities/lineItem/lineItemHelper";
import { routes } from "utilities/routes";
import { formattedPlural } from "utilities/string/string";
import { isTaxGroupIdValid } from "utilities/tax/tax.utilities";

import { BTAlert } from "commonComponents/btWrappers/BTAlert/BTAlert";
import { BTButton } from "commonComponents/btWrappers/BTButton/BTButton";
import { btDeleteConfirm } from "commonComponents/btWrappers/BTConfirm/BTConfirm";
import { BTDatePickerDate } from "commonComponents/btWrappers/BTDatePicker/BTDatePickerDate";
import { BTForm, BTFormItem, BTFormItemAutomatic } from "commonComponents/btWrappers/BTForm/BTForm";
import { BTSelect } from "commonComponents/btWrappers/BTSelect/BTSelect";
import { BTSelectEditable } from "commonComponents/btWrappers/BTSelectEditable/BTSelectEditable";
import { BTSplitView } from "commonComponents/btWrappers/BTSplitView/BTSplitView";
import { ActivityLog } from "commonComponents/entity/activityLog/ActivityLog";
import CommentComponent from "commonComponents/entity/comment/CommentComponent";
import { isOpenCommentsEnabled } from "commonComponents/entity/comment/CommentComponent.utils";
import {
    type DiscussionsEntity,
    type IHasDiscussionContainer,
} from "commonComponents/entity/discussion/DiscussionContainer/DiscussionContainer.api.types";
import { AttachmentContainer } from "commonComponents/entity/media/AttachmentContainer/AttachmentContainer";
import { type ISharingConfig } from "commonComponents/entity/sharing/EntityShareButton/EntityShareButton";
import FeedbackEmbeddableIcon from "commonComponents/feedback/FeedbackEmbeddableIcon/FeedbackEmbeddableIcon";
import { DomesticTaxRateSelect } from "commonComponents/financial/TaxRateSelect/DomesticTaxRateSelect";
import { getDetailPath } from "commonComponents/utilities/CollectionEditable/CollectionEditable";
import { showUnsavedChangesPrompt } from "commonComponents/utilities/DirtyTracking/UnsavedChangesPrompt";
import {
    type IBTInjectedFormikProps,
    withFormikBT,
} from "commonComponents/utilities/Formik/WithFormikBT";
import {
    APPLY_DOCUMENT_PARSE_RESULT_TO_FORM_SUCCESS_MESSAGE,
    getSelectionOptionStatusDisplay,
    INVALID_PRODUCT_URL_MESSAGE,
    isUnsupportedProductUrlHost,
    UNSUPPORTED_PRODUCT_URL_MESSAGE,
} from "commonComponents/utilities/SelectionOption/SelectionOption.utils";

import { CategoryDetail } from "entity/category/Category/Category";
import { CategoryType } from "entity/category/common/categoryType";
import { convertFilesToAttachedFilesRequest } from "entity/media/common/media.utils";
import { RouteOwnerInvoice } from "entity/ownerInvoice/OwnerInvoice/OwnerInvoiceRoute";
import { RoutePurchaseOrder } from "entity/purchaseOrder/PurchaseOrder/PurchaseOrderRoute";
import { RouteSelectionOptionPOMapping } from "entity/selection/SelectionPOMapping/SelectionPOMappingRoute";
import {
    type SelectionChoiceLineItem,
    type SelectionOptionLineItem,
} from "entity/selectionChoice/SelectionChoice/SelectionChoice.api.types";
import { getLineItemSelectionOptionsSchema } from "entity/selectionChoice/SelectionChoiceLineItemContainer/SelectionChoiceLineItemContainer.types";
import {
    AutofillOptionModal,
    type AutofillOptionModalSubmission,
} from "entity/selectionOption/common/AutofillModal/AutofillOptionModal";
import {
    applyDocumentParseResultToForm,
    applyParseUrlResultToForm,
    getAllowanceCostCodeId,
} from "entity/selectionOption/common/SelectionOption.utils";
import { SelectionOptionModalHeaderSlotContext } from "entity/selectionOption/modal/SelectionOptionModalHeaderSlotContext";
import SelectionOptionLineItemContainer, {
    type SelectionOptionColumnOrder,
} from "entity/selectionOption/SelectionOptionLineItemContainer/SelectionOptionLineItemContainer";
import { mapSelectionOptionGroupsToTreeData } from "entity/selectionOption/selectionOptionsUtilities";
import {
    applyAllowanceCostCodeDefault,
    type ISelectionOptionEntity,
} from "entity/selectionOptions/SelectionOption/SelectionOption";
import {
    type IAutofillTempFileData,
    type ISelectionOptionAutofillState,
} from "entity/selectionOptions/SelectionOption/SelectionOption.types";
import { SpecificationsLinkButton } from "entity/specification/common/SpecificationsPanel/SpecificationsLinkButton";
import { SpecificationsPanelLayout } from "entity/specification/common/SpecificationsPanel/SpecificationsPanelLayout";
import { JobTaxVersion } from "entity/tax/common/tax.types";
import { NoTaxId } from "entity/tax/TaxRate/TaxRate.api.types";

import "./SelectionOptionPresentational.less";

import { SelectionOptionHeader } from "./SelectionOptionHeader/SelectionOptionHeader";

const DescriptionLabel = () => <BTLabel text="Description" />;
const SelectionOptionGroupLabel = () => <BTLabel text="Allowance" />;
const CategoryLabel = () => <BTLabel text="Category" />;
const LocationLabel = () => <BTLabel text="Location" />;
const DueDateLabel = () => <BTLabel text="Due date" />;
const ProductUrlLabel = () => <BTLabel text="Product URL" />;

enum PanelKey {
    Comments = "Comments",
    PlansAndSpecs = "PlansAndSpecs",
    ActivityLog = "ActivityLog",
}

// dimension.AppServerPersistedSettings (description=EnableSelectionChoiceAndOptionTracking) was enabled
// in prod 2026-05-28 18:40 UTC per its history table; +1 day cushion past the partial-day toggle.
const SELECTION_OPTION_TRACKING_START_DATE = new Date("2026-05-29");

export const SelectionOptionValidators = yup.object().shape<ISelectionOptionFormValues>({
    title: yup.string().trim().required("Required").max(100).label("Title"),
    description: yup.string().max(2500).label("Description"),
    selectionOptionLineItems: yup
        .array<SelectionOptionLineItem>(
            getLineItemSelectionOptionsSchema() as yup.ObjectSchema<SelectionOptionLineItem>,
        )
        .label("Selection Option Line Items"),
    selectionOptionGroupId: yup.number().nullable().label("Selection Option Group"),
    categoryId: yup.number().nullable().label("Category"),
    locationId: yup.number().nullable().label("Location"),
    attachedFiles: yup.mixed().label("Attached Files"),
    productUrl: yup.string().url().nullable().label("Product URL"),
    imageUrl: yup.string().url().nullable(),
    deadlineDate: yup.mixed().notRequired().label("Due date"),
});

export interface ISelectionOptionFormValues {
    description?: string;
    title: string;
    selectionOptionLineItems: SelectionChoiceLineItem[];
    lineItems?: LineItem[];
    hasDeletedLineItems?: boolean;
    selectionOptionGroups?: SelectionOptionGroup[];
    selectionOptionGroupId?: number | null;
    categoryId?: number | null;
    locationId?: number | null;
    attachedFiles: AttachedFilesRequest;
    productUrl?: string;
    imageUrl?: string;
    deadlineDate?: Date;
    /**
     * Set when the user populated the form via the AI "Autofill from URL" action. Tracked as a
     * form value so it participates in Formik dirty tracking and resets on form remount; passed
     * through on create to stamp the new option's origin as AI-suggested.
     */
    wasAutofillUsed?: boolean;
}

export type SelectionOptionSaveAction = "undefined" | "save" | "saveAndNew" | "saveAndClose";

/**
 * Imperative unsaved-changes guard, mirroring `ISpecificationViewerRef` so both catalog tabs use
 * one pattern. `guardNavigation` runs `action` immediately when the form is clean; when it is dirty
 * it prompts and only runs `action` after a successful save or a discard.
 */
export interface ISelectionOptionGuardRef {
    guardNavigation: (action: () => void) => void;
}

export type SelectionOptionActions = SelectionOptionSaveAction | "checkedActions";

export interface ISelectionOptionPresentationalProps extends RouteComponentProps {
    entity: ISelectionOptionEntity;
    isBuilder: boolean;
    isExistingEntity: boolean;
    attachedFiles: AttachedFiles | null;
    /**
     * Overrides the document instance type used when building the attached-files entity.
     * Defaults to {@link DocumentInstanceType.SelectionOption}. Catalog options pass
     * {@link DocumentInstanceType.SelectionCatalogOption} so uploads/deletes target the
     * correct entity.
     */
    attachmentDocumentInstanceType?: DocumentInstanceType;
    hasDeletePermission: boolean;
    hasUpdatePermission: boolean;
    hasCreatePermission: boolean;
    isCreating: boolean;
    isDeleting: boolean;
    isEditing: boolean;
    isLoading: boolean;
    isModal?: boolean;
    isUpdating: boolean;
    /** Null when rendering a catalog option (no job context). */
    jobId: number | null;
    jobName: string;
    onDelete?: (id: number) => Promise<void>;
    onEdit: () => void;
    onExitEditMode: () => void;
    onGoBack: () => void;
    onPanelClose?: () => void;
    onSave: (
        values: ISelectionOptionFormValues,
        action: SelectionOptionSaveAction,
    ) => Promise<void>;
    openCreate: () => void;
    openDetails: (id: number) => void;
    readOnly: boolean;
    selectionOptionId?: number;
    builderInfo: BuilderInfo;
    onApprove?: (
        values: ISelectionOptionFormValues,
        loginType: BTLoginTypes,
        isDirty: boolean,
    ) => Promise<void>;
    onDecline?: (
        values: ISelectionOptionFormValues,
        loginType: BTLoginTypes,
        isDirty: boolean,
    ) => Promise<void>;
    onReset?: (
        values: ISelectionOptionFormValues,
        loginType: BTLoginTypes,
        isDirty: boolean,
    ) => Promise<void>;
    onSend?: (
        values: ISelectionOptionFormValues,
        loginType: BTLoginTypes,
        isDirty: boolean,
    ) => Promise<void>;
    onCopy: () => Promise<void>;
    canSaveToCatalog: boolean;
    onSaveToCatalog: (values: ISelectionOptionFormValues, isDirty: boolean) => Promise<void>;
    isSavingToCatalog: boolean;
    canCreatePurchaseOrder?: boolean;
    canAddToExistingPurchaseOrder?: boolean;
    /** Called after the PO mapping modal successfully completes (reaches the completion step) */
    onPoMappingComplete?: () => void;
    /** Optional allowlist forwarded to SelectionOptionLineItemContainer. */
    visibleLineItemColumns?: ReadonlySet<number>;
    /**
     * Suppresses the Comments and Activity Log header actions and their split-view panels.
     * Set by SelectionOptionDetailPanel for the allowance-drawer context.
     */
    hideCommentsAndActivityLog?: boolean;
    /**
     * Parses a product URL via the AI service and returns the extracted fields together with the
     * predicted cost code id, or null when the parse failed (the container surfaces the error
     * toast). Only invoked from the Create flow.
     */
    parseProductUrl: (url: string) => Promise<{
        fields: ParsedUrlSelectionOptionFields;
        predictedCostCodeId: number | null;
    } | null>;
    isParsingProductUrl: boolean;
    /** Hydrated discussion container for this option (undefined while loading or for new options). */
    discussionEntity?: IHasDiscussionContainer;
    /** Refetches the discussion thread after a comment is added/edited/deleted. */
    onUpdateCommentData?: (comments: DiscussionsEntity) => Promise<void>;
    /**
     * Autofill state from the "Autofill from URL" modal. When present, applied once on mount
     * via applyParseResultToForm to pre-populate title, description, and line items.
     * Only used for new options; defaults are guaranteed loaded before this component mounts.
     */
    initialAutofillState?: ISelectionOptionAutofillState | null;
    /**
     * One-shot intent from the list's comment icon: when true, the detail mounts with the Comments
     * panel expanded. Sourced from router state by the container, so it doesn't survive a refresh.
     */
    openComments?: boolean;
    /**
     * Receives this form's unsaved-changes guard so a host (e.g. the Selections catalog tab) can
     * run its own navigation through the prompt. A plain prop rather than a real `ref` because
     * `withFormikBT` wraps this component in a function component that doesn't forward refs.
     */
    guardRef?: React.Ref<ISelectionOptionGuardRef>;
}

function SelectionOptionPresentationalInternal(
    props: IBTInjectedFormikProps<ISelectionOptionPresentationalProps, ISelectionOptionFormValues>,
) {
    const {
        dirty,
        entity,
        errors,
        isBuilder,
        isDeleting,
        isExistingEntity,
        isLoading,
        isUpdating,
        isModal = false,
        selectionOptionId,
        setFieldTouched,
        setFieldValue,
        setSubmitting,
        touched,
        values,
        jobId,
        attachedFiles,
        visibleLineItemColumns,
    } = props;

    const hasJob = jobId != null;
    const hasStatus = entity.status != null;
    const hasExternalId = !!entity.externalId;

    const coBlockMessage = (() => {
        const coStatus = entity.linkedChangeOrderStatus;
        if (coStatus == null) return null;
        if (coStatus === ChangeOrderStatus.APPROVED || coStatus === ChangeOrderStatus.MANUAL)
            return null;
        return "This allowance was created via a change order. Selections cannot be sent until the change order is approved.";
    })();

    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const builderInfo = useRequiredContext(BuilderInfoContext);
    const userInfo = useRequiredContext(UserInfoContext);
    const appDefaultValues = useRequiredContext(AppDefaultInfoContext);
    const modalHeaderSlot = useContext(SelectionOptionModalHeaderSlotContext);

    // Specs and comments share the BTSplitView detail slot — opening one closes the other.
    // When the first prop-carrying panel lands, migrate this to a discriminated union with
    // per-key `props` payloads — see `ActivePanel` in
    // entity/budget/JobCostingBudget/JobCostingBudget.tsx for the reference shape.
    // `openComments` covers in-app navigation from the list's comment icon (router state); the
    // `commentId` query param covers deep links from the comment "Open conversation" link, which
    // mirrors how ChangeOrderPresentational seeds its comments panel from the same query param.
    const [activePanel, setActivePanel] = useState<PanelKey | undefined>(() =>
        props.openComments || isOpenCommentsEnabled(searchParams) ? PanelKey.Comments : undefined,
    );

    const togglePanel = useCallback((key: PanelKey) => {
        setActivePanel((curr) => (curr === key ? undefined : key));
    }, []);

    const closePanel = useCallback(() => {
        setActivePanel(undefined);
    }, []);

    const handleCommentsClick = useCallback(() => togglePanel(PanelKey.Comments), [togglePanel]);
    const handlePlansAndSpecsToggle = useCallback(
        () => togglePanel(PanelKey.PlansAndSpecs),
        [togglePanel],
    );
    const handleActivityLogToggle = useCallback(
        () => togglePanel(PanelKey.ActivityLog),
        [togglePanel],
    );

    const isBusy =
        props.isSubmitting || isLoading || isDeleting || isUpdating || props.isSavingToCatalog;
    const entityStatus = entity.status ?? 0;

    const isBuilderPortal = isBuilder;
    const isClient =
        userInfo.loginType === BTLoginTypes.OWNER || userInfo.loginType === BTLoginTypes.CONTACT;
    const canAddOrEdit = isBuilder && (props.isCreating || props.isEditing);
    const { text: statusDisplayText, type: statusType } = getSelectionOptionStatusDisplay(
        !isLoading && isExistingEntity ? entityStatus : 0,
        isBuilderPortal,
        { isClient, deadlineDate: entity.deadlineDate },
    );
    const statusMessage = hasStatus ? statusDisplayText : "";

    // Last saved form values - used to reset fields on cancel
    const [savedValues, setSavedValues] = useState<ISelectionOptionFormValues>({
        title: entity.title ?? "",
        description: entity.description ?? "",
        selectionOptionLineItems: entity.selectionOptionLineItems ?? [],
        selectionOptionGroupId: entity.selectionOptionGroupId ?? null,
        categoryId: entity.categoryId ?? null,
        locationId: entity.locationId ?? null,
        attachedFiles: new AttachedFilesRequest(),
        productUrl: entity.productUrl,
        imageUrl: entity.imageUrl,
        deadlineDate: entity.deadlineDate ?? undefined,
    });

    // Category tree data for BTSelectEditable
    const [categoryTreeData, setCategoryTreeData] = useState<BTSelectItem[]>(
        entity.categories ?? [],
    );

    const handleCategoryTreeDataChange = useCallback(
        (newCollection: BTSelectItem[], changedEntity: BTSelectItem) => {
            const isStillInCollection = newCollection.some(
                (item) => item.value === changedEntity.value,
            );
            const newCategoryId = isStillInCollection
                ? (changedEntity.value as number | null)
                : null;

            // Keep "None" (null value) at top, sort the rest alphabetically
            const noneItem = newCollection.find((item) => item.value === null);
            const rest = newCollection
                .filter((item) => item.value !== null)
                .sort((a, b) => a.title.toLowerCase().localeCompare(b.title.toLowerCase()));

            const sorted = noneItem ? [noneItem, ...rest] : rest;

            // Update selection state so the correct item shows as selected
            const updated = sorted.map(
                (item) =>
                    new BTSelectItem({
                        ...item,
                        selected: item.value === newCategoryId,
                    }),
            );

            setCategoryTreeData(updated);
            setFieldValue("categoryId", newCategoryId);
        },
        [setFieldValue],
    );

    // Location tree data for BTSelectEditable
    const [locationTreeData, setLocationTreeData] = useState<BTSelectItem[]>(
        entity.locations ?? [],
    );

    const handleLocationTreeDataChange = useCallback(
        (newCollection: BTSelectItem[], changedEntity: BTSelectItem) => {
            const isStillInCollection = newCollection.some(
                (item) => item.value === changedEntity.value,
            );
            const newLocationId = isStillInCollection
                ? (changedEntity.value as number | null)
                : null;

            // Keep "None" (null value) at top, sort the rest alphabetically
            const noneItem = newCollection.find((item) => item.value === null);
            const rest = newCollection
                .filter((item) => item.value !== null)
                .sort((a, b) => a.title.toLowerCase().localeCompare(b.title.toLowerCase()));

            const sorted = noneItem ? [noneItem, ...rest] : rest;

            // Update selection state so the correct item shows as selected
            const updated = sorted.map(
                (item) =>
                    new BTSelectItem({
                        ...item,
                        selected: item.value === newLocationId,
                    }),
            );

            setLocationTreeData(updated);
            setFieldValue("locationId", newLocationId);
        },
        [setFieldValue],
    );

    // Track line items that should break their link on save
    const [lineItemIdsThatBreakLinkOnSave, setLineItemIdsThatBreakLinkOnSave] = useState<
        Set<number>
    >(new Set());

    const handleLineItemShouldBreakLink = useCallback(
        (lineItemId: number, shouldBreak: boolean) => {
            setLineItemIdsThatBreakLinkOnSave((prev) => {
                const next = new Set(prev);
                if (shouldBreak) {
                    next.add(lineItemId);
                } else {
                    next.delete(lineItemId);
                }
                return next;
            });
        },
        [],
    );

    // Re-derive lineItemDefaults whenever the form's selected allowance changes so the
    // costCodeOptions list marks the cost code mapped to that allowance as selected.
    const effectiveLineItemDefaults = useMemo(
        () =>
            applyAllowanceCostCodeDefault(
                entity.lineItemDefaults,
                values.selectionOptionGroupId,
                entity.costCodeIdsByGroupId,
            ) ?? entity.lineItemDefaults,
        [entity.lineItemDefaults, entity.costCodeIdsByGroupId, values.selectionOptionGroupId],
    );

    const { parseProductUrl, isParsingProductUrl } = props;

    // Seeded true so the skeleton shows on the first render for an autofill navigation (URL or
    // document), before the on-load effect fires — avoids a flash of the empty form. Cleared when
    // the parse resolves.
    const [isAutofillingOnLoad, setIsAutofillingOnLoad] = useState(
        () =>
            !!props.isCreating &&
            (!!props.initialAutofillState?.autofillTempFile ||
                !!props.initialAutofillState?.productUrl),
    );
    // Tracks the in-place parse triggered from the AutofillModal (banner entry point), as
    // opposed to isAutofillingOnLoad which only covers the initial router-state driven parse.
    const [isAutofillingFromModal, setIsAutofillingFromModal] = useState(false);
    const isParsingAutofill = isParsingProductUrl || isAutofillingOnLoad || isAutofillingFromModal;
    const [showAutofillModal, setShowAutofillModal] = useState(false);
    const [autofillSource, setAutofillSource] = useState<"url" | "document" | undefined>(undefined);

    // define skeletons for autofill loading
    const inputSkeleton = (testid: string, height: number) => (
        <div data-testid={testid}>
            <Skeleton active title={false} paragraph={{ rows: height }} />
        </div>
    );

    const handleParseProductUrl = useCallback(async () => {
        const productUrl = values.productUrl?.trim();
        if (!productUrl) return;
        const result = await parseProductUrl(productUrl);
        if (!result) return;
        applyParseUrlResultToForm(
            result.fields,
            setFieldValue,
            effectiveLineItemDefaults,
            props.entity.taxGroupId,
            props.entity.effectiveTaxVersion,
            result.predictedCostCodeId,
            getAllowanceCostCodeId(values.selectionOptionGroupId, entity.costCodeIdsByGroupId),
        );
        setAutofillSource("url");
        setFieldTouched("title", true, false);
        setFieldTouched("description", true, false);
        setFieldTouched("selectionOptionLineItems", true, false);
    }, [
        parseProductUrl,
        values.productUrl,
        values.selectionOptionGroupId,
        setFieldValue,
        setFieldTouched,
        effectiveLineItemDefaults,
        entity.costCodeIdsByGroupId,
        props.entity.taxGroupId,
        props.entity.effectiveTaxVersion,
    ]);

    const hasMappingCompletedRef = useRef(false);
    const hasAppliedAutofillRef = useRef(false);

    // Parses an uploaded document (temp file) and applies the AI-extracted fields to the form.
    const parseDocumentAndApplyToForm = useCallback(
        async (autofillTempFile: IAutofillTempFileData) => {
            try {
                const response = await selectionOptionsParseDocumentsPostV2({
                    jobId: jobId!,
                    files: [
                        {
                            tempFileId: autofillTempFile.id,
                            fileName: autofillTempFile.fileName,
                            contentType: autofillTempFile.contentType,
                            fileLength: autofillTempFile.fileSize,
                        },
                    ],
                });
                const result = response.data?.results?.[0];
                const selectionOptions = result?.selectionOptions ?? [];

                // The endpoint returns 200 with status="error" for soft failures (e.g. no
                // usable fields extracted); surface the friendly message it provides.
                if (result?.status === "error") {
                    void message.error(
                        result.errorMessage ?? "Failed to parse document. Please try again.",
                    );
                    return;
                }

                if (selectionOptions.length > 0) {
                    // Handle return of multiple selection options from single document
                    if (selectionOptions.length > 1) {
                        reportError(
                            new Error("Multiple selection options scraped from single document"),
                        );
                        void message.warning(
                            "Multiple selection options were scraped from the document. Only the first option was applied.",
                        );
                    }

                    const firstOption = selectionOptions[0];

                    if (firstOption) {
                        applyDocumentParseResultToForm(
                            firstOption,
                            setFieldValue,
                            effectiveLineItemDefaults,
                            props.entity.taxGroupId,
                            props.entity.effectiveTaxVersion,
                            getAllowanceCostCodeId(
                                values.selectionOptionGroupId,
                                entity.costCodeIdsByGroupId,
                            ),
                        );
                        setAutofillSource("document");
                        // The multi-option case already surfaces a warning above; a clean
                        // single-option parse gets the success toast instead.
                        if (selectionOptions.length === 1) {
                            void message.success(
                                APPLY_DOCUMENT_PARSE_RESULT_TO_FORM_SUCCESS_MESSAGE,
                            );
                        }
                    }
                }
            } catch (e) {
                reportError(e);
                void message.error("Failed to parse document. Please try again.");
            }
        },
        [
            jobId,
            setFieldValue,
            effectiveLineItemDefaults,
            props.entity.taxGroupId,
            props.entity.effectiveTaxVersion,
            values.selectionOptionGroupId,
            entity.costCodeIdsByGroupId,
        ],
    );

    useEffect(() => {
        if (hasAppliedAutofillRef.current || !props.initialAutofillState || !props.isCreating)
            return;
        hasAppliedAutofillRef.current = true;
        setFieldValue("productUrl", props.initialAutofillState.productUrl);

        if (props.initialAutofillState.autofillTempFile) {
            void parseDocumentAndApplyToForm(props.initialAutofillState.autofillTempFile).finally(
                () => {
                    setIsAutofillingOnLoad(false);
                },
            );
            setAutofillSource("document");
        } else if (props.initialAutofillState.productUrl) {
            // URL autofill: parse on load (mirrors the document flow); skeleton already showing.
            // parseProductUrl handles its own success/error toasts and returns null on failure.
            void parseProductUrl(props.initialAutofillState.productUrl)
                .then((result) => {
                    if (result) {
                        applyParseUrlResultToForm(
                            result.fields,
                            setFieldValue,
                            effectiveLineItemDefaults,
                            props.entity.taxGroupId,
                            props.entity.effectiveTaxVersion,
                            result.predictedCostCodeId,
                            getAllowanceCostCodeId(
                                values.selectionOptionGroupId,
                                entity.costCodeIdsByGroupId,
                            ),
                        );
                        setAutofillSource("url");
                    }
                })
                .finally(() => {
                    setIsAutofillingOnLoad(false);
                });
        }
    }, [
        parseDocumentAndApplyToForm,
        effectiveLineItemDefaults,
        entity.costCodeIdsByGroupId,
        jobId,
        parseProductUrl,
        props.entity.effectiveTaxVersion,
        props.entity.taxGroupId,
        props.initialAutofillState,
        setFieldValue,
        props.isCreating,
        values.selectionOptionGroupId,
    ]);

    const modalConfig = {
        beforeClose: () => {
            if (hasMappingCompletedRef.current) {
                props.onPoMappingComplete?.();
                hasMappingCompletedRef.current = false;
            }

            // Use navigate(-1) instead of props.history.goBack() so this always operates on the real browser history
            navigate(-1);
        },
        parentRoute: props.match?.url ?? "",
    };

    // Sync state when entity loads or changes (navigation, refresh, new entity)
    // We only depend on selectionOptionId and not entity fields - if we depend on
    //  entity fields, useEffect can fire after save when entity object
    // reference changes (even with stale data), overwriting savedValues before the next edit
    useEffect(() => {
        setSavedValues({
            title: entity.title ?? "",
            description: entity.description ?? "",
            selectionOptionLineItems: entity.selectionOptionLineItems ?? [],
            selectionOptionGroupId: entity.selectionOptionGroupId ?? null,
            categoryId: entity.categoryId ?? null,
            locationId: entity.locationId ?? null,
            attachedFiles: new AttachedFilesRequest(),
        });
        setCategoryTreeData(entity.categories ?? []);
        setLocationTreeData(entity.locations ?? []);
        // intentionally using entity values without dependencies
        // We want this to run only when selectionOptionId changes (navigation/new entity),
        // not on every entity reference change
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectionOptionId]);

    const validateFormFields = async (): Promise<boolean> => {
        const formErrors = await props.validateForm();
        if (Object.keys(formErrors).length > 0) {
            if (formErrors.title) {
                setFieldTouched("title", true, false);
            }
            if (formErrors.description) {
                setFieldTouched("description", true, false);
            }
            if (formErrors.productUrl) {
                setFieldTouched("productUrl", true, false);
            }
            return false;
        }
        return true;
    };

    /**
     * Updates saved values after a successful save/approve/decline
     */
    const updateSavedState = (title: string = values.title) => {
        setSavedValues({
            title,
            description: values.description ?? "",
            selectionOptionLineItems: values.selectionOptionLineItems ?? [],
            selectionOptionGroupId: values.selectionOptionGroupId ?? null,
            categoryId: values.categoryId ?? null,
            locationId: values.locationId ?? null,
            attachedFiles: values.attachedFiles,
            productUrl: values.productUrl,
            imageUrl: values.imageUrl,
            deadlineDate: values.deadlineDate,
        });
    };

    const handleSave = async (action: SelectionOptionSaveAction): Promise<boolean> => {
        if (!(await validateFormFields())) {
            return false;
        }

        setSubmitting(true);
        try {
            try {
                await props.onSave(
                    {
                        title: values.title,
                        description: values.description,
                        selectionOptionLineItems: values.selectionOptionLineItems,
                        hasDeletedLineItems: values.hasDeletedLineItems,
                        selectionOptionGroupId: values.selectionOptionGroupId,
                        categoryId: values.categoryId,
                        locationId: values.locationId,
                        attachedFiles: values.attachedFiles,
                        productUrl: values.productUrl,
                        imageUrl: values.imageUrl,
                        deadlineDate: values.deadlineDate,
                        wasAutofillUsed: values.wasAutofillUsed,
                    },
                    action,
                );
            } catch {
                // The container surfaces its own error toast. Report the failure rather than
                // rethrowing so guarded navigation can keep the user on their unsaved edit.
                return false;
            }

            // Store saved values for cancel behavior
            updateSavedState();

            // Options that stay in edit mode after saving never re-derive their initial values, so
            // without an explicit reset Formik reads dirty forever. That covers Pending/Draft job
            // options and catalog options, which have no workflow status and no view mode at all.
            if (
                entity.status === Statuses.PENDING ||
                entity.status === Statuses.UNRELEASED ||
                entity.status == null
            ) {
                // Update line items that broke their cost catalog link after save
                let resetValues = { ...values };
                if (lineItemIdsThatBreakLinkOnSave.size > 0) {
                    const updatedLineItems = values.selectionOptionLineItems.map((li) =>
                        lineItemIdsThatBreakLinkOnSave.has(li.id)
                            ? { ...li, costItemId: null }
                            : li,
                    );
                    resetValues = { ...resetValues, selectionOptionLineItems: updatedLineItems };
                    setLineItemIdsThatBreakLinkOnSave(new Set());
                }
                props.resetForm(resetValues);
            }
        } finally {
            setSubmitting(false);
        }

        return true;
    };

    // Prompt when dirty, not loading, and we aren't in the view state.
    // formik doesn't get reinitialized going from edit -> view.
    const hasUnsavedChanges = () => dirty && !isLoading && (props.isEditing || props.isCreating);

    const resetFieldsToSaved = () => {
        setFieldValue("title", savedValues.title);
        setFieldValue("description", savedValues.description);
        setFieldValue("selectionOptionLineItems", savedValues.selectionOptionLineItems);
        setFieldValue("selectionOptionGroupId", savedValues.selectionOptionGroupId);
        setFieldValue("categoryId", savedValues.categoryId);
        setFieldValue("locationId", savedValues.locationId);
        setFieldValue("productUrl", savedValues.productUrl);
        setFieldValue("imageUrl", savedValues.imageUrl);
    };

    const handleCancel = (cancelMethod: "Cancel" | "Backlink" | "PanelClose") => {
        const exit = () => {
            if (cancelMethod === "PanelClose") {
                props.onPanelClose?.();
            } else if (props.isEditing && cancelMethod !== "Backlink") {
                props.onExitEditMode();
            } else {
                props.onGoBack();
            }
        };
        if (hasUnsavedChanges()) {
            showUnsavedChangesPrompt([], {
                // Awaited for the same reason as guardNavigation below: btConfirm dismisses the
                // prompt as soon as onOk settles, so returning early would leave the form
                // interactive and still dirty while the write is in flight.
                onCloseWithSave: async () => {
                    await handleSave("saveAndClose");
                },
                onCloseWithoutSave: () => {
                    resetFieldsToSaved();
                    exit();
                },
                closable: false,
            });
        } else {
            exit();
        }
    };

    // Breadcrumb links navigate to a fixed destination (job page / selections list) rather than
    // going back, so they route through the same unsaved-changes guard as Cancel/back: when the
    // form is dirty we suppress the link's default navigation, prompt, and only navigate on
    // confirm. When clean, the link navigates normally.
    const handleBreadcrumbNavigate = (event: MouseEvent<HTMLAnchorElement>, to: string) => {
        if (!hasUnsavedChanges()) {
            return;
        }
        event.preventDefault();
        showUnsavedChangesPrompt([], {
            onCloseWithSave: () => {
                void handleSave("save").then((saved) => {
                    if (saved) {
                        navigate(to);
                    }
                });
            },
            onCloseWithoutSave: () => {
                resetFieldsToSaved();
                navigate(to);
            },
            closable: false,
        });
    };

    // Host-driven navigation (catalog row switch, Catalog modal close) runs through the same guard
    // as the breadcrumbs. "save" rather than "saveAndClose": the caller owns what happens next, so
    // asking the container to close as well would navigate on top of `action`.
    const guardNavigation = (action: () => void) => {
        if (!hasUnsavedChanges()) {
            action();
            return;
        }
        showUnsavedChangesPrompt([], {
            // Awaited, not fire-and-forget: btConfirm dismisses the prompt as soon as onOk settles,
            // so returning early would leave the Catalog interactive with the PUT still in flight —
            // a second X or row click would then re-prompt about an edit that is already saving.
            onCloseWithSave: async () => {
                if (await handleSave("save")) {
                    action();
                }
            },
            onCloseWithoutSave: () => {
                resetFieldsToSaved();
                action();
            },
            closable: false,
        });
    };

    useImperativeHandle(props.guardRef, () => ({ guardNavigation }));

    const handleDeleteSelectionOption = async () => {
        if (selectionOptionId && props.onDelete) {
            await props.onDelete(selectionOptionId);
        }
    };

    /**
     * handler for status change actions (approve, decline, reset)
     * @param callback - function to execute (onApprove, onDecline, or onReset)
     */
    const handleStatusAction = async (
        callback?: (
            values: ISelectionOptionFormValues,
            loginType: BTLoginTypes,
            isDirty: boolean,
        ) => Promise<void>,
    ) => {
        if (!(await validateFormFields())) {
            return;
        }

        setSubmitting(true);
        try {
            if (callback) {
                await callback(
                    {
                        title: values.title,
                        description: values.description,
                        selectionOptionLineItems: values.selectionOptionLineItems,
                        hasDeletedLineItems: values.hasDeletedLineItems,
                        selectionOptionGroupId: values.selectionOptionGroupId,
                        categoryId: values.categoryId,
                        locationId: values.locationId,
                        attachedFiles: values.attachedFiles,
                        productUrl: values.productUrl,
                        imageUrl: values.imageUrl,
                        deadlineDate: values.deadlineDate,
                    },
                    userInfo.loginType,
                    dirty,
                );

                // Update display title and saved values after successful action
                updateSavedState();
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleSaveToCatalog = async () => {
        // Validate when the form is open so required fields can't be skipped
        // by clicking "Save to catalog" before saving.
        if (props.isEditing && !(await validateFormFields())) {
            return;
        }

        setSubmitting(true);
        try {
            await props.onSaveToCatalog(
                {
                    title: values.title,
                    description: values.description,
                    selectionOptionLineItems: values.selectionOptionLineItems,
                    hasDeletedLineItems: values.hasDeletedLineItems,
                    selectionOptionGroupId: values.selectionOptionGroupId,
                    categoryId: values.categoryId,
                    locationId: values.locationId,
                    attachedFiles: values.attachedFiles,
                    productUrl: values.productUrl,
                    imageUrl: values.imageUrl,
                    deadlineDate: values.deadlineDate,
                },
                props.isEditing && dirty,
            );

            // If a save ran as part of the catalog flow, refresh saved changes and
            // reset Formik's dirty tracking so the unsaved changes prompt doesn't fire
            if (props.isEditing && dirty) {
                updateSavedState();
                props.resetForm(values);
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleApprove = async () => {
        if (coBlockMessage) {
            void message.warning(coBlockMessage);
            return;
        }
        await handleStatusAction(props.onApprove);
    };

    const handleDecline = async () => {
        if (coBlockMessage) {
            void message.warning(coBlockMessage);
            return;
        }
        await handleStatusAction(props.onDecline);
    };

    const handleReset = async () => {
        await handleStatusAction(props.onReset);
    };

    const handleSend = async () => {
        if (coBlockMessage) {
            void message.warning(coBlockMessage);
            return;
        }
        await handleStatusAction(props.onSend);
    };

    const handleDeleteLineItems = (lineItemIds: number[], partialDelete: boolean = false) => {
        const { values, setFieldValue } = props;
        const setOfDeletedLineItemIds = new Set(lineItemIds);
        const updatedLineItems = (values.selectionOptionLineItems ?? [])
            .map((li) => {
                const newLineItem = { ...li };
                return newLineItem as SelectionOptionLineItem;
            })
            .filter((li) => !setOfDeletedLineItemIds.has(li.id));

        setFieldValue("selectionOptionLineItems", updatedLineItems);
        setFieldValue("lineItems", updatedLineItems);
        setFieldValue("hasDeletedLineItems", true);

        values.selectionOptionLineItems = updatedLineItems ?? [];
        values.hasDeletedLineItems = true;

        if (partialDelete) {
            void message.warning("Some of the selected items were not deleted.", 5);
        } else {
            void message.success(
                `${formattedPlural({
                    value: lineItemIds.length,
                    one: "Item",
                    other: "Items",
                })} deleted`,
                5,
            );
        }
    };

    const handleDeleteLineItemsWithConfirm = (lineItems: SelectionOptionLineItem[]) => {
        if (lineItems.length > 0) {
            btDeleteConfirm({
                entityName: formattedPlural({
                    value: lineItems.length,
                    one: "Item",
                    other: "Items",
                }),
                content: `Are you sure you want to delete the selected Selection Option ${formattedPlural(
                    {
                        value: lineItems.length,
                        one: "item",
                        other: "items",
                    },
                )}?`,
                onOk: () => {
                    const lineItemsToDelete = lineItems.map((x) => x.id);
                    handleDeleteLineItems(
                        lineItemsToDelete,
                        lineItemsToDelete.length !== lineItems.length,
                    );
                },
            });
        } else {
            handleDeleteLineItems(lineItems.map((x) => x.id));
        }
    };

    const handleAddToNewPurchaseOrder = (toExisting: boolean) => {
        if (selectionOptionId && hasJob) {
            navigate(
                modalConfig.parentRoute +
                    routes.poMapping.getSelectionOptionLink(selectionOptionId, jobId, toExisting),
            );
        }
    };

    const lineItemDerivedTaxGroupId = useMemo(() => {
        const lineItems = values.selectionOptionLineItems ?? [];
        if (lineItems.length === 0) {
            return entity.taxGroupId ?? NoTaxId;
        }
        return lineItems.find((li) => isTaxGroupIdValid(li.taxGroupId))?.taxGroupId ?? NoTaxId;
    }, [values.selectionOptionLineItems, entity.taxGroupId]);

    const showTaxes =
        hasJob && (entity?.effectiveTaxVersion ?? JobTaxVersion.None) >= JobTaxVersion.V1;

    // Holds the temp file staged for display in the attachments section before save
    const [stagedAutofillTempFile, setStagedAutofillTempFile] = useState<
        IAutofillTempFileData | undefined
    >(() => props.initialAutofillState?.autofillTempFile);

    const autofillAttachedFiles = useMemo<AttachedFiles | null>(() => {
        if (!stagedAutofillTempFile) return null;
        return new AttachedFiles({
            files: [expandAutofillTempFile(stagedAutofillTempFile, attachedFiles?.newFileOptions)],
            viewingPermissions: attachedFiles?.viewingPermissions,
            newFileOptions: attachedFiles?.newFileOptions,
        });
    }, [stagedAutofillTempFile, attachedFiles?.newFileOptions, attachedFiles?.viewingPermissions]);

    const displayAttachedFiles =
        props.isCreating && autofillAttachedFiles ? autofillAttachedFiles : attachedFiles;

    const handleAutofillFromUrl = useCallback(
        async (url: string) => {
            setFieldValue("productUrl", url);
            setIsAutofillingFromModal(true);
            try {
                const result = await parseProductUrl(url);
                if (!result) return;
                applyParseUrlResultToForm(
                    result.fields,
                    setFieldValue,
                    effectiveLineItemDefaults,
                    props.entity.taxGroupId,
                    props.entity.effectiveTaxVersion,
                    result.predictedCostCodeId,
                    getAllowanceCostCodeId(
                        values.selectionOptionGroupId,
                        entity.costCodeIdsByGroupId,
                    ),
                );
                setAutofillSource("url");
                setFieldTouched("title", true, false);
                setFieldTouched("description", true, false);
                setFieldTouched("selectionOptionLineItems", true, false);
            } finally {
                setIsAutofillingFromModal(false);
            }
        },
        [
            parseProductUrl,
            setFieldValue,
            setFieldTouched,
            effectiveLineItemDefaults,
            props.entity.taxGroupId,
            props.entity.effectiveTaxVersion,
            values.selectionOptionGroupId,
            entity.costCodeIdsByGroupId,
        ],
    );

    const handleAutofillFromDocument = useCallback(
        async (tempFile: IAutofillTempFileData) => {
            setStagedAutofillTempFile(tempFile);
            setFieldValue(
                "attachedFiles",
                convertFilesToAttachedFilesRequest([
                    new BTFileSystem(
                        expandAutofillTempFile(tempFile, attachedFiles?.newFileOptions),
                    ),
                ]),
            );
            setAutofillSource("document");
            setIsAutofillingFromModal(true);
            try {
                await parseDocumentAndApplyToForm(tempFile);
            } finally {
                setIsAutofillingFromModal(false);
            }
        },
        [setFieldValue, attachedFiles?.newFileOptions, parseDocumentAndApplyToForm],
    );

    const handleAutofillModalSubmit = useCallback(
        (submission: AutofillOptionModalSubmission) => {
            setShowAutofillModal(false);
            if (submission.type === "url") {
                void handleAutofillFromUrl(submission.url);
            } else {
                void handleAutofillFromDocument(submission.tempFile);
            }
        },
        [handleAutofillFromUrl, handleAutofillFromDocument],
    );

    const attachedFilesEntity = useMemo(
        () =>
            displayAttachedFiles != null
                ? {
                      documentInstanceType:
                          props.attachmentDocumentInstanceType ??
                          DocumentInstanceType.SelectionOption,
                      name: "Selection Option",
                      id: selectionOptionId ?? 0,
                      jobId: jobId ?? undefined,
                      builderId: builderInfo.builderId,
                      attachedFiles: displayAttachedFiles,
                  }
                : null,
        [
            displayAttachedFiles,
            builderInfo.builderId,
            jobId,
            props.attachmentDocumentInstanceType,
            selectionOptionId,
        ],
    );

    const showAttachmentsSection =
        displayAttachedFiles != null &&
        attachedFilesEntity != null &&
        (attachedFilesEntity.attachedFiles.files.length > 0 ||
            attachedFilesEntity.attachedFiles.viewingPermissions.canAddFiles);

    const { hideCommentsAndActivityLog = false } = props;

    const showPlansAndSpecsPanel =
        activePanel === PanelKey.PlansAndSpecs && hasExternalId && hasJob;
    const showCommentsPanel = activePanel === PanelKey.Comments && !hideCommentsAndActivityLog;
    const showActivityLogPanel =
        activePanel === PanelKey.ActivityLog && !hideCommentsAndActivityLog;

    const hasLineItems =
        Array.isArray(entity.selectionOptionLineItems) &&
        (entity.selectionOptionLineItems?.length ?? 0) > 0;
    const showPurchaseOrderActions =
        hasJob && isBuilder && !isModal && entity.status === Statuses.APPROVED && hasLineItems;

    const canCopy = isExistingEntity && isBuilder && props.hasCreatePermission && hasStatus;

    const canOpenProductUrl =
        values.productUrl && yup.string().url().isValidSync(values.productUrl);
    const showProductUrl = canOpenProductUrl || props.isCreating || props.isEditing;
    const isUnsupportedAutofillHost = isUnsupportedProductUrlHost(values.productUrl);
    // Autofill needs a non-empty, well-formed URL to scrape. Empty or malformed input
    // leaves the button disabled so we never send an unusable URL to the parser.
    const isProductUrlValidForAutofill =
        !!values.productUrl?.trim() && yup.string().url().isValidSync(values.productUrl);
    const hasSelectionOptionDocumentScraping = Boolean(
        builderInfo.flags.selectionOptionDocumentScraping,
    );
    const showAutofillBanner = props.isCreating && hasSelectionOptionDocumentScraping;

    // Feedback is reported against whichever source actually populated the form, so document
    // autofills land under their own FeedbackTarget instead of the URL flow's.
    const isDocumentAutofill = autofillSource === "document";
    const autofillFeedbackTarget = isDocumentAutofill
        ? FeedbackTarget.SelectionAutofillFromDocument
        : FeedbackTarget.SelectionAutofillFromUrl;
    const autofillFeedbackExtraInfo = JSON.stringify(
        isDocumentAutofill
            ? {
                  jobId,
                  fileName: stagedAutofillTempFile?.fileName,
                  contentType: stagedAutofillTempFile?.contentType,
                  workflowStep: "autofill-from-document",
              }
            : {
                  jobId,
                  productUrl: values.productUrl,
                  workflowStep: "autofill-banner",
              },
    );

    // The header renders the comments + activity toolbar icons itself (via BTItemHeader's
    // CommentButton and the `activity` mapped icon) so they collapse into the overflow menu on
    // mobile. `disabled` covers the loading window before the discussion fetch resolves so users
    // don't open an empty panel.
    const hasComments = (props.discussionEntity?.discussions.comments.comments.length ?? 0) > 0;
    const commentsConfig =
        isExistingEntity && !hideCommentsAndActivityLog
            ? {
                  hasComments,
                  hasUnreadComments: !!props.discussionEntity?.discussions.oldestUnreadCommentId,
                  isDrawerOpen: activePanel === PanelKey.Comments,
                  onClick: handleCommentsClick,
                  disabled: !props.discussionEntity,
              }
            : undefined;

    const showActivityLog =
        isBuilder &&
        appDefaultValues.enableSelectionChoiceAndOptionTracking &&
        !hideCommentsAndActivityLog &&
        (userInfo.permissions?.selections.canViewPrice ?? false);
    const activityLogConfig = showActivityLog
        ? {
              isOpen: activePanel === PanelKey.ActivityLog,
              onToggle: handleActivityLogToggle,
          }
        : undefined;

    const sharingConfig = useMemo<ISharingConfig | undefined>(() => {
        if (!selectionOptionId || !hasJob) {
            return undefined;
        }
        return {
            entity: {
                entityType: EntityTypes.SelectionOptions,
                id: selectionOptionId,
                organizationId: builderInfo.builderId,
                jobId,
            },
        };
    }, [selectionOptionId, builderInfo.builderId, jobId, hasJob]);

    // Shown under the title once touched, mirroring WarrantyPresentational's required-title pattern.
    const titleError = touched.title && errors.title ? errors.title : undefined;

    const headerNode = (
        <SelectionOptionHeader
            comments={commentsConfig}
            activityLog={activityLogConfig}
            disabled={isBusy}
            isParsingProductUrl={isParsingAutofill}
            titleError={titleError}
            hasDeletePermission={props.hasDeletePermission}
            hasUpdatePermission={props.hasUpdatePermission}
            isCreating={props.isCreating}
            isEditing={props.isEditing}
            isExistingEntity={isExistingEntity}
            isModal={isModal}
            jobId={jobId ?? 0}
            jobName={props.jobName}
            onBack={() => handleCancel("Backlink")}
            onPanelClose={props.onPanelClose ? () => handleCancel("PanelClose") : undefined}
            onCancel={() => handleCancel("Cancel")}
            onBreadcrumbNavigate={handleBreadcrumbNavigate}
            onDelete={() => void handleDeleteSelectionOption()}
            onEdit={props.onEdit}
            onSave={() => void handleSave("save")}
            statusMessage={statusMessage}
            statusTestId="statusTestId"
            statusType={statusType}
            titleValue={values.title}
            titleReadOnly={props.readOnly || isBusy}
            onTitleChange={setFieldValue}
            onTitleBlur={setFieldTouched}
            builderInfo={builderInfo}
            status={entity.status ?? undefined}
            onApprove={() => void handleApprove()}
            onDecline={() => void handleDecline()}
            onReset={() => void handleReset()}
            onSend={() => void handleSend()}
            onCopy={props.onCopy}
            canCopy={canCopy}
            canSaveToCatalog={props.canSaveToCatalog}
            onSaveToCatalog={() => void handleSaveToCatalog()}
            isSavingToCatalog={props.isSavingToCatalog}
            canCreatePurchaseOrder={showPurchaseOrderActions && entity.canCreatePurchaseOrder}
            canAddToExistingPurchaseOrder={
                showPurchaseOrderActions && entity.canAddToExistingPurchaseOrder
            }
            onOpenPurchaseOrderLinkingWizard={handleAddToNewPurchaseOrder}
            sharing={sharingConfig}
            coBlockMessage={coBlockMessage}
        />
    );
    const selectionOptionHeader =
        isModal && modalHeaderSlot ? createPortal(headerNode, modalHeaderSlot) : headerNode;

    const jobRoutes = hasJob && (
        <>
            <RouteSelectionOptionPOMapping
                modalConfig={modalConfig}
                onComplete={() => {
                    hasMappingCompletedRef.current = true;
                }}
            />
            <RoutePurchaseOrder modalConfig={modalConfig} />
            <RouteOwnerInvoice modalConfig={modalConfig} />
        </>
    );

    const bodyContent = (
        <div className="selectionOptionBodyContainer">
            {showAutofillBanner && (
                <BTAlert
                    data-testid="selectionOptionAutofillBanner"
                    type="info"
                    message={
                        <span className="flex items-center gap-2">
                            <IconSparkle />
                            Autofill from a product URL or document to skip manual entry.
                        </span>
                    }
                    className="margin-bottom-sm"
                    action={
                        values.wasAutofillUsed ? (
                            <FeedbackEmbeddableIcon
                                feedbackTarget={autofillFeedbackTarget}
                                feedbackText="How was autofilling this selection option?"
                                extraInfo={autofillFeedbackExtraInfo}
                            />
                        ) : (
                            <BTButton
                                data-testid="selectionOptionAutofillBannerButton"
                                type="secondary"
                                disabled={isParsingAutofill}
                                onClick={() => setShowAutofillModal(true)}
                            >
                                Autofill
                            </BTButton>
                        )
                    }
                />
            )}
            {/* Parent is intentionally HCF with child as 1 to stop children from expanding horizontally with parent AND child as displayType = 1 */}
            <BdsLayout
                displayType="HCF"
                id="selectionOptionMainContent"
                testid="selectionOptionMainContent"
                className="SelectionOptionMainContent"
            >
                <BdsLayout
                    displayType={hasJob || showAttachmentsSection ? "1and1" : "1"}
                    id="selectionOptionFormSection"
                    testid="selectionOptionFormSection"
                >
                    <BdsSection
                        id="selectionOptionFormFields"
                        className="selection-option-form-fields"
                    >
                        <BTText
                            text="Details"
                            size="distinct-md"
                            tag="h2"
                            className={cn({
                                "margin-bottom-md": !props.isEditing,
                            })}
                        />
                        <BTForm>
                            {hasJob && (
                                <BTFormItemAutomatic<ISelectionOptionFormValues>
                                    id="deadlineDate"
                                    label={DueDateLabel}
                                >
                                    <BTDatePickerDate
                                        id="deadlineDate"
                                        data-testid="deadlineDate"
                                        value={values.deadlineDate}
                                        onChange={setFieldValue}
                                        disabledDate={(current) =>
                                            !!current && isBefore(current, startOfDay(new Date()))
                                        }
                                        readOnly={props.readOnly || isBusy}
                                    />
                                </BTFormItemAutomatic>
                            )}
                            <BTFormItemAutomatic<ISelectionOptionFormValues>
                                id="description"
                                label={DescriptionLabel}
                            >
                                {isParsingAutofill ? (
                                    inputSkeleton("descriptionSkeleton", 3)
                                ) : (
                                    <BTTextArea
                                        aria-label="Description"
                                        data-testid="description"
                                        disabled={isBusy}
                                        id="description"
                                        className="selection-option-description-textarea"
                                        onBlur={(field: string | number, isTouched: boolean) =>
                                            props.setFieldTouched(String(field), isTouched)
                                        }
                                        onChange={(field: string | number, value: string) =>
                                            props.setFieldValue(
                                                String(field) as "title" | "description",
                                                value,
                                            )
                                        }
                                        readOnly={props.readOnly}
                                        rows={4}
                                        value={values.description}
                                    />
                                )}
                            </BTFormItemAutomatic>
                            <BTFormItemAutomatic<ISelectionOptionFormValues>
                                id="selectionOptionGroupId"
                                label={SelectionOptionGroupLabel}
                            >
                                <BTSelect
                                    id="selectionOptionGroupId"
                                    data-testid="selectionOptionGroupId"
                                    disabled={!props.isCreating && !props.isEditing}
                                    treeData={mapSelectionOptionGroupsToTreeData(
                                        entity.selectionOptionGroups ?? [],
                                        entity.selectionOptionGroupId ?? null,
                                    )}
                                    value={values.selectionOptionGroupId}
                                    onChange={(_field: string | number, value: number) =>
                                        props.setFieldValue("selectionOptionGroupId", value)
                                    }
                                    onBlur={(_field: string | number) =>
                                        props.setFieldTouched("selectionOptionGroupId", true)
                                    }
                                    readOnly={props.readOnly}
                                    bds
                                />
                            </BTFormItemAutomatic>
                            <div className="flex flex-row category-location-row">
                                <div className="flex flex-col">
                                    <BTFormItemAutomatic<ISelectionOptionFormValues>
                                        id="categoryId"
                                        label={CategoryLabel}
                                    >
                                        <BTSelectEditable<ISelectionOptionFormValues>
                                            id="categoryId"
                                            data-testid="categoryId"
                                            treeData={categoryTreeData}
                                            value={values.categoryId}
                                            onChange={setFieldValue}
                                            onBlur={setFieldTouched}
                                            onTreeDataChange={handleCategoryTreeDataChange}
                                            shouldAlphabetizeOnTreeDataChange={false}
                                            detailPath={routes.category.getDetailsRoute(
                                                CategoryType.SelectionCategories,
                                            )}
                                            addRoute={getEditModeLink(
                                                getDetailPath(
                                                    props.match,
                                                    routes.category.getCategoryLink(
                                                        0,
                                                        CategoryType.SelectionCategories,
                                                    ),
                                                ),
                                            )}
                                            canAdd={canAddOrEdit}
                                            canEdit={canAddOrEdit}
                                            editRoute={(selectedItem) =>
                                                getEditModeLink(
                                                    getDetailPath(
                                                        props.match,
                                                        routes.category.getCategoryLink(
                                                            selectedItem.value as number,
                                                            CategoryType.SelectionCategories,
                                                        ),
                                                    ),
                                                )
                                            }
                                            useBds
                                            readOnly={props.readOnly}
                                            disabled={isBusy}
                                            renderDetail={(detailProps) => {
                                                const catId =
                                                    (detailProps.match.params.catId &&
                                                        parseInt(detailProps.match.params.catId)) ||
                                                    0;
                                                return (
                                                    <CategoryDetail
                                                        id={catId}
                                                        categoryType={
                                                            CategoryType.SelectionCategories
                                                        }
                                                        onAdd={(bti) => {
                                                            detailProps.add(new BTSelectItem(bti));
                                                            detailProps.navigateBack();
                                                        }}
                                                        onEdit={(bti) => {
                                                            detailProps.edit(new BTSelectItem(bti));
                                                            detailProps.navigateBack();
                                                        }}
                                                        onDelete={(id) => {
                                                            detailProps.delete(
                                                                new BTSelectItem({
                                                                    id,
                                                                }),
                                                            );
                                                            detailProps.navigateBackForDelete();
                                                        }}
                                                        beforeClose={detailProps.navigateBack}
                                                    />
                                                );
                                            }}
                                        />
                                    </BTFormItemAutomatic>
                                </div>
                                <div className="flex flex-col">
                                    <BTFormItemAutomatic<ISelectionOptionFormValues>
                                        id="locationId"
                                        label={LocationLabel}
                                    >
                                        <BTSelectEditable<ISelectionOptionFormValues>
                                            id="locationId"
                                            data-testid="locationId"
                                            treeData={locationTreeData}
                                            value={values.locationId}
                                            onChange={setFieldValue}
                                            onBlur={setFieldTouched}
                                            onTreeDataChange={handleLocationTreeDataChange}
                                            shouldAlphabetizeOnTreeDataChange={false}
                                            detailPath={routes.category.getDetailsRoute(
                                                CategoryType.SelectionLocations,
                                            )}
                                            addRoute={getEditModeLink(
                                                getDetailPath(
                                                    props.match,
                                                    routes.category.getCategoryLink(
                                                        0,
                                                        CategoryType.SelectionLocations,
                                                    ),
                                                ),
                                            )}
                                            canAdd={canAddOrEdit}
                                            canEdit={canAddOrEdit}
                                            editRoute={(selectedItem) =>
                                                getEditModeLink(
                                                    getDetailPath(
                                                        props.match,
                                                        routes.category.getCategoryLink(
                                                            selectedItem.value as number,
                                                            CategoryType.SelectionLocations,
                                                        ),
                                                    ),
                                                )
                                            }
                                            useBds
                                            readOnly={props.readOnly}
                                            disabled={isBusy}
                                            renderDetail={(detailProps) => {
                                                const catId =
                                                    (detailProps.match.params.catId &&
                                                        parseInt(detailProps.match.params.catId)) ||
                                                    0;
                                                return (
                                                    <CategoryDetail
                                                        id={catId}
                                                        categoryType={
                                                            CategoryType.SelectionLocations
                                                        }
                                                        onAdd={(bti) => {
                                                            detailProps.add(new BTSelectItem(bti));
                                                            detailProps.navigateBack();
                                                        }}
                                                        onEdit={(bti) => {
                                                            detailProps.edit(new BTSelectItem(bti));
                                                            detailProps.navigateBack();
                                                        }}
                                                        onDelete={(id) => {
                                                            detailProps.delete(
                                                                new BTSelectItem({
                                                                    id,
                                                                }),
                                                            );
                                                            detailProps.navigateBackForDelete();
                                                        }}
                                                        beforeClose={detailProps.navigateBack}
                                                    />
                                                );
                                            }}
                                        />
                                    </BTFormItemAutomatic>
                                </div>
                            </div>
                            {showProductUrl && (
                                <BTFormItemAutomatic<ISelectionOptionFormValues>
                                    id="productUrl"
                                    label={ProductUrlLabel}
                                >
                                    {isParsingAutofill ? (
                                        inputSkeleton("urlInputSkeleton", 1)
                                    ) : (
                                        <>
                                            {props.isEditing || props.isCreating ? (
                                                <div className="flex flex-row gap-1 min-w-full">
                                                    <BTInput
                                                        aria-label="Product URL"
                                                        id="productUrl"
                                                        data-testid="productUrl"
                                                        disabled={isBusy}
                                                        value={values.productUrl}
                                                        onChange={props.setFieldValue}
                                                        onBlur={props.setFieldTouched}
                                                        readOnly={props.readOnly}
                                                    />
                                                    <BdsLink
                                                        testid="productUrlIconLink"
                                                        to={values.productUrl}
                                                        disabled={!canOpenProductUrl}
                                                        externalLink
                                                        target="_blank"
                                                    >
                                                        <BTPopover
                                                            content="Open product URL in a new tab"
                                                            data-testid="productUrlLinkBtnTooltip"
                                                            placement="bottomRight"
                                                        >
                                                            <BTButton
                                                                aria-label="Product URL Link"
                                                                data-testid="productUrlLinkBtn"
                                                                type="tertiary"
                                                                icon={<IconArrowSquareOut />}
                                                                disabled={!canOpenProductUrl}
                                                            />
                                                        </BTPopover>
                                                    </BdsLink>
                                                    {props.isCreating &&
                                                        !hasSelectionOptionDocumentScraping && (
                                                            <BTPopover
                                                                content={
                                                                    isUnsupportedAutofillHost
                                                                        ? UNSUPPORTED_PRODUCT_URL_MESSAGE
                                                                        : !isProductUrlValidForAutofill
                                                                          ? INVALID_PRODUCT_URL_MESSAGE
                                                                          : "Autofill from URL"
                                                                }
                                                                data-testid="productUrlParseBtnTooltip"
                                                                placement="bottomRight"
                                                            >
                                                                <BTButton
                                                                    aria-label="Autofill selection option from URL"
                                                                    data-testid="productUrlParseBtn"
                                                                    type="primary"
                                                                    icon={<IconSparkle />}
                                                                    loading={isParsingAutofill}
                                                                    disabled={
                                                                        isBusy ||
                                                                        isParsingAutofill ||
                                                                        isUnsupportedAutofillHost ||
                                                                        !isProductUrlValidForAutofill
                                                                    }
                                                                    onClick={handleParseProductUrl}
                                                                >
                                                                    Autofill
                                                                </BTButton>
                                                            </BTPopover>
                                                        )}
                                                    {/* Surfaced only after an autofill scrape populates the form */}
                                                    {props.isCreating &&
                                                        values.wasAutofillUsed &&
                                                        !hasSelectionOptionDocumentScraping && (
                                                            <FeedbackEmbeddableIcon
                                                                feedbackTarget={
                                                                    FeedbackTarget.SelectionAutofillFromUrl
                                                                }
                                                                feedbackText="How was autofilling this selection?"
                                                                extraInfo={JSON.stringify({
                                                                    jobId,
                                                                    productUrl: values.productUrl,
                                                                    workflowStep:
                                                                        "autofill-from-url",
                                                                })}
                                                            />
                                                        )}
                                                </div>
                                            ) : (
                                                <BdsLink
                                                    className="selection-option-product-url-link"
                                                    testid="productUrlLink"
                                                    to={values.productUrl}
                                                    externalLink
                                                    target="_blank"
                                                >
                                                    {values.productUrl}
                                                </BdsLink>
                                            )}
                                        </>
                                    )}
                                </BTFormItemAutomatic>
                            )}

                            {hasJob && (
                                <BTFormItem
                                    label={
                                        <BTText
                                            text="Plans and Specs"
                                            size="distinct-md"
                                            tag="h2"
                                            className={cn({
                                                "margin-bottom-md": !props.isEditing,
                                            })}
                                        />
                                    }
                                >
                                    <SpecificationsLinkButton
                                        externalId={entity.externalId}
                                        canEdit={props.hasUpdatePermission}
                                        isPanelOpen={showPlansAndSpecsPanel}
                                        onTogglePanel={handlePlansAndSpecsToggle}
                                        sourceEntityType={EntityType.SelectionOptions}
                                    />
                                </BTFormItem>
                            )}
                        </BTForm>
                    </BdsSection>
                    {(hasJob || showAttachmentsSection) && (
                        <BdsSection
                            id="selectionOptionApprovalStatusColumn"
                            className="selection-option-approval-status-column"
                        >
                            <div>
                                {isParsingProductUrl ? (
                                    <div className="selection-option-hero-image-wrapper">
                                        <div
                                            data-testid="heroImageSkeleton"
                                            className="selection-option-hero-image selection-option-hero-skeleton"
                                        />
                                    </div>
                                ) : (
                                    values.imageUrl && (
                                        <div className="selection-option-hero-image-wrapper">
                                            <img
                                                src={values.imageUrl}
                                                alt="Selection Option Product"
                                                data-testid="heroImage"
                                                className="selection-option-hero-image"
                                            />
                                            {(props.isCreating || props.isEditing) && (
                                                <BTButton
                                                    aria-label="Remove image"
                                                    data-testid="removeImageUrlBtn"
                                                    type="tertiary"
                                                    icon={<IconTrash />}
                                                    className="selection-option-hero-image-delete-btn"
                                                    onClick={() =>
                                                        setFieldValue("imageUrl", undefined)
                                                    }
                                                />
                                            )}
                                        </div>
                                    )
                                )}
                                {showAttachmentsSection && (
                                    <BTForm>
                                        <BTFormItemAutomatic
                                            id="attachedFiles"
                                            label={
                                                <BTText
                                                    text="Images and attachments"
                                                    size="distinct-md"
                                                    tag="h2"
                                                    className={cn({
                                                        "margin-bottom-md": !props.isEditing,
                                                    })}
                                                />
                                            }
                                        >
                                            <AttachmentContainer
                                                entity={attachedFilesEntity}
                                                attachedFiles={displayAttachedFiles}
                                                onChangeForNewEntity={setFieldValue}
                                                idForFiles="attachedFiles"
                                                isReadOnly={props.readOnly}
                                            />
                                        </BTFormItemAutomatic>
                                    </BTForm>
                                )}
                            </div>
                        </BdsSection>
                    )}
                </BdsLayout>
                <BTSection
                    id="selectionOptionPriceDetails"
                    className="p-0 selection-option-price-details"
                >
                    <BTText
                        text="Price details"
                        size="distinct-md"
                        tag="h2"
                        className="padding-bottom-xs"
                    />
                    {showTaxes && (
                        <DomesticTaxRateSelect
                            isReadOnly
                            treeData={getTaxGroupSelectItemsFromLineItemDefaults(
                                effectiveLineItemDefaults,
                            )}
                            taxGroupId={lineItemDerivedTaxGroupId}
                            onBlur={() => {}}
                            setFieldValue={() => {}}
                            id="taxGroupId"
                            data-testid="taxGroupId"
                            label="Taxes"
                            useBds
                        />
                    )}
                    {isParsingAutofill ? (
                        inputSkeleton("lineItemsSkeleton", 3)
                    ) : (
                        <SelectionOptionLineItemContainer
                            {...props}
                            jobId={jobId ?? 0}
                            canEdit={props.hasUpdatePermission && !props.readOnly}
                            canEditCostCodes={props.hasUpdatePermission && !props.readOnly}
                            validationSchema={SelectionOptionValidators}
                            builderInfo={props.builderInfo}
                            lineItemDefaults={effectiveLineItemDefaults}
                            isEditing={props.isEditing || props.isCreating}
                            taxGroupId={props.entity.taxGroupId}
                            effectiveTaxVersion={props.entity.effectiveTaxVersion}
                            onDeleteLineItems={handleDeleteLineItemsWithConfirm}
                            onLineItemShouldBreakLink={handleLineItemShouldBreakLink}
                            visibleColumns={
                                visibleLineItemColumns as
                                    | ReadonlySet<SelectionOptionColumnOrder>
                                    | undefined
                            }
                        />
                    )}
                </BTSection>
            </BdsLayout>
        </div>
    );

    return (
        <>
            {jobRoutes}
            {showAutofillBanner && (
                <AutofillOptionModal
                    jobId={jobId ?? 0}
                    visible={showAutofillModal}
                    initialProductUrl={values.productUrl}
                    onCancel={() => setShowAutofillModal(false)}
                    onSubmit={handleAutofillModalSubmit}
                />
            )}
            <BdsLayout
                displayType="HCF"
                useMaxWidth
                contentAlign="compact"
                id="selectionOptionLayout"
                testid="selectionOptionLayout"
                className={cn("SelectionOptionPresentational", {
                    "SelectionOptionPresentational--splitOpen":
                        showPlansAndSpecsPanel || showCommentsPanel || showActivityLogPanel,
                })}
            >
                {selectionOptionHeader}
                <BTSplitView
                    testid="selectionOptionPanel"
                    name="Selection Option"
                    mode={
                        showPlansAndSpecsPanel || showCommentsPanel || showActivityLogPanel
                            ? "split"
                            : "list"
                    }
                    defaultDetailWidth={600}
                    minDetailWidth={320}
                    maxDetailSize={50}
                    persistenceId="selection-option-specs-panel"
                    list={bodyContent}
                    detail={
                        showActivityLogPanel ? (
                            <ActivityLog
                                externalEntityId={entity.externalId ?? ""}
                                entityId={selectionOptionId ?? 0}
                                externalBuilderId={entity.externalBuilderId ?? ""}
                                externalJobId={entity.externalJobId ?? ""}
                                entityType={EntityTypes.SelectionOptions}
                                createdDateTime={entity.addedByDate}
                                createdBy={entity.addedByName ?? undefined}
                                entityTrackingDate={SELECTION_OPTION_TRACKING_START_DATE}
                                onClose={closePanel}
                            />
                        ) : showCommentsPanel ? (
                            <div
                                className="selection-option-comments-panel"
                                data-testid="selectionOptionCommentsPanel"
                            >
                                <CommentComponent
                                    entity={props.discussionEntity}
                                    setCommentDrawerOpen={(isOpen) => {
                                        if (!isOpen) {
                                            closePanel();
                                        }
                                    }}
                                    onUpdateCommentData={props.onUpdateCommentData}
                                    isLoading={!props.discussionEntity}
                                    defaultTabVisibility={{ canShowSubs: false }}
                                />
                            </div>
                        ) : showPlansAndSpecsPanel ? (
                            <SpecificationsPanelLayout
                                externalId={entity.externalId!}
                                sourceEntityType={EntityType.SelectionOptions}
                                jobId={jobId ?? 0}
                                canEdit={props.hasUpdatePermission}
                                onClose={closePanel}
                                hostProvidesPurchaseOrderRoute
                            />
                        ) : null
                    }
                />
            </BdsLayout>
        </>
    );
}

function expandAutofillTempFile(
    tempFile: IAutofillTempFileData,
    newFileOptions: FileViewOptions | undefined,
) {
    const { showOwner } = getFileViewOptionDefaults(newFileOptions);
    return {
        ...tempFile,
        documentInstanceId: String(tempFile.id),
        isTempFile: true as const,
        isOnNewEntity: true as const,
        viewingPermissions: { showOwner, showSubs: false, showAllSubs: false, mainFile: false },
        permissions: getPermissionsOverride({ canDelete: true }),
    };
}

export const SelectionOptionPresentational = withFormikBT<
    ISelectionOptionPresentationalProps,
    ISelectionOptionFormValues
>({
    mapPropsToValues: (props: ISelectionOptionPresentationalProps) => {
        const tempFile = props.isCreating
            ? props.initialAutofillState?.autofillTempFile
            : undefined;
        return {
            title: props.entity?.title ?? "",
            description: props.entity?.description ?? "",
            selectionOptionLineItems: props.entity?.selectionOptionLineItems ?? [],
            hasDeletedLineItems: false,
            selectionOptionGroupId: props.entity?.selectionOptionGroupId ?? null,
            categoryId: props.entity?.categoryId ?? null,
            locationId: props.entity?.locationId ?? null,
            attachedFiles: tempFile
                ? convertFilesToAttachedFilesRequest([
                      new BTFileSystem(
                          expandAutofillTempFile(tempFile, props.attachedFiles?.newFileOptions),
                      ),
                  ])
                : new AttachedFilesRequest(),
            productUrl: props.entity?.productUrl,
            imageUrl: props.entity?.imageUrl,
            deadlineDate: props.entity?.deadlineDate ?? undefined,
        };
    },
    validationSchema: () => SelectionOptionValidators,
    validateOnChange: true,
    validateOnBlur: true,
    enableReinitialize: true,
    enableDirtyTracking: true,
    handleSubmit: () => {},
})(SelectionOptionPresentationalInternal);

export default SelectionOptionPresentational;
